import { Flex, Table, TableProps, Typography, Dropdown } from "antd";
import type { ColumnType, SorterResult, SortOrder } from "antd/es/table/interface";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useMemo, useCallback, memo, ReactNode } from "react";
import { DownOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";

import { ListStoreConstructor } from "stores/ListStoreConstructor";
import { CustomButton, DateSearchInput, SearchInput, WhiteBoxBlock } from "ui";
import { ORDER_MAP } from "interfaces";
import { TableError, EmptyTable } from "./EmptyTable";

import styles from "./table.module.css";
import { hasNonDefaultFiltersApplied } from "./utils";

const SORT_DIRECTIONS: SortOrder[] = ["ascend", "descend", null];

const PageSizeMenu = memo(
  ({
    pageSize,
    pageSizeOptions,
    pageSizeDropdownVisible,
    onOpenChange,
    onSizeChange,
  }: {
    pageSize: number;
    pageSizeOptions: number[];
    pageSizeDropdownVisible: boolean;
    onOpenChange: (visible: boolean) => void;
    onSizeChange: (value: number) => void;
  }) => {
    const pageSizeMenuItems = useMemo(
      () =>
        pageSizeOptions.map((size) => ({
          key: size,
          label: size,
          onClick: () => onSizeChange(size),
        })),
      [pageSizeOptions, onSizeChange]
    );

    return (
      <div className={styles.pageSizeChanger}>
        <Dropdown
          menu={{ items: pageSizeMenuItems }}
          trigger={["click"]}
          open={pageSizeDropdownVisible}
          onOpenChange={onOpenChange}
          destroyPopupOnHide={false}
        >
          <div className={styles.fullClickableArea}>
            <Typography.Text strong>Показывать по </Typography.Text>
            <Typography.Text strong className={styles.valueText}>
              {pageSize}
            </Typography.Text>
            <DownOutlined className={styles.arrowIcon} />
          </div>
        </Dropdown>
      </div>
    );
  }
);

export interface ICommonTableParams extends TableProps<any> {
  store: ListStoreConstructor<any>;
  updateWithInterval?: boolean;
  widgets?: {
    search: boolean;
    date: boolean;
    searchPlaceholder?: string;
  };
  whiteBox?: boolean;
  removeButtonAlign?: "start" | "end";
  additionalElements?: ReactNode;
  hintsIds?: Record<string, string>;
  customShouldShowResetButton?: () => boolean;
  resetFiltersOnClear?: () => void;
}

export const CommonTable = observer((params: ICommonTableParams) => {
  const {
    store,
    updateWithInterval,
    widgets,
    whiteBox,
    removeButtonAlign = "right",
    additionalElements,
    hintsIds,
    customShouldShowResetButton,
    resetFiltersOnClear,
    ...rest
  } = params;

  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);

  const [pageSizeDropdownVisible, setPageSizeDropdownVisible] = useState<boolean>(false);

  const [tableKey, setTableKey] = useState(uuid());

  useEffect(() => {
    const fetchInterval = setInterval(() => {
      if (updateWithInterval) {
        store.fetch({ isSilent: true });
      } else {
        clearInterval(fetchInterval);
      }
    }, 30000);

    store.fetch();

    return () => clearInterval(fetchInterval);
  }, [store, updateWithInterval]);

  const handleResetFilters = useCallback(() => {
    store.removeFilters();
    setActiveFilterColumn(null);
    setTableKey(uuid());
    resetFiltersOnClear?.();
  }, [store, resetFiltersOnClear]);

  const shouldShowResetButton = customShouldShowResetButton
    ? customShouldShowResetButton()
    : hasNonDefaultFiltersApplied(store);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      store.onChangeDataFlow({
        paginationData: {
          currentPage: 1,
          pageSize: value,
        },
        filters: store.filters,
        sorters: store.sorters,
      });
    },
    [store]
  );

  const handlePageSizeVisibleChange = useCallback((visible: boolean) => {
    setPageSizeDropdownVisible(visible);
  }, []);

  const handleFilterDropdownVisibleChange = useCallback(
    (visible: boolean, dataIndex: string | number | readonly string[]) => {
      if (typeof dataIndex === "string") {
        setActiveFilterColumn(visible ? dataIndex : null);
      }
    },
    []
  );

  const pageSize = store.paginationData.pageSize || 10;
  const pageSizeOptions = [10, 20, 50, 100];

  const pageSizeMenuMemoized = useMemo(
    () => (
      <PageSizeMenu
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pageSizeDropdownVisible={pageSizeDropdownVisible}
        onOpenChange={handlePageSizeVisibleChange}
        onSizeChange={handlePageSizeChange}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSize, pageSizeDropdownVisible]
  );

  const columns = useMemo(() => {
    return rest.columns?.map((col: ColumnType<any>) => {
      const dataIndex = col.dataIndex;
      const isStringIndex = typeof dataIndex === "string";
      const key = isStringIndex ? (col.key as string) || dataIndex : undefined;

      const sortOrder =
        isStringIndex && store.sorters[dataIndex as string]
          ? store.sorters[dataIndex as string] === "asc"
            ? ("ascend" as SortOrder)
            : store.sorters[dataIndex as string] === "desc"
              ? ("descend" as SortOrder)
              : null
          : null;

      const filteredValue = key ? store.filters[key] : undefined;

      return {
        ...col,
        title: typeof col.title === "string" ? <span className={styles.headerTitle}>{col.title}</span> : col.title,
        sortDirections: SORT_DIRECTIONS,
        sortOrder: sortOrder,
        ...(key && { filteredValue, filtered: filteredValue !== undefined }),
        filterDropdownProps: isStringIndex
          ? {
              open: activeFilterColumn === dataIndex,
              onOpenChange: (visible: boolean) => handleFilterDropdownVisibleChange(visible, dataIndex as string),
            }
          : undefined,
      };
    });
  }, [rest.columns, store.sorters, store.filters, activeFilterColumn]);

  const paginationConfig = useMemo(
    () => ({
      showSizeChanger: false,
      total: store.paginationData.totalElements,
      current: store.paginationData.currentPage,
      pageSize: pageSize,
      className: styles.pagination,
      position: ["bottomRight"] as "bottomRight"[],
    }),
    [store.paginationData.totalElements, store.paginationData.currentPage, pageSize]
  );

  const localeConfig = useMemo(
    () => ({
      emptyText:
        store.fetchStatus === "error" ? (
          <TableError store={store} />
        ) : (
          <EmptyTable store={store} hasFiltersApplied={shouldShowResetButton} />
        ),
    }),
    [store.fetchStatus, store, shouldShowResetButton]
  );

  const tableContent = useMemo(
    () => (
      <div className={styles.tableWrapper}>
        <Table
          scroll={{ x: "max-content" }}
          key={tableKey}
          rowKey="id"
          {...rest}
          dataSource={store.data}
          showSorterTooltip={false}
          className={`${styles.customTable} ${rest.className || ""}`}
          onChange={(params, filters, sorter, extra) => {
            let sorters: { [key: string]: "asc" | "desc" | undefined } = {};

            if (Array.isArray(sorter)) {
              sorter.forEach((item: SorterResult<any>) => {
                const field = typeof item.field === "string" ? item.field : null;
                if (field && item.order) {
                  sorters[field] = ORDER_MAP[item.order];
                }
              });
            } else if (sorter) {
              if (sorter.field === undefined) {
                store.onChangeDataFlow({
                  paginationData: {
                    currentPage: params.current,
                    pageSize: params.pageSize,
                  },
                  filters: filters,
                  sorters: {},
                });
                return;
              } else {
                const field = typeof sorter.field === "string" ? sorter.field : null;
                if (field && sorter.order) {
                  sorters = { [field]: ORDER_MAP[sorter.order] };
                }
              }
            }

            store.onChangeDataFlow({
              paginationData: {
                currentPage: params.current,
                pageSize: params.pageSize,
              },
              filters: filters,
              sorters,
            });
          }}
          columns={columns}
          loading={store.isPending}
          pagination={paginationConfig}
          locale={localeConfig}
        />
        {store.allData.length > 0 && <div className={styles.paginationFooter}>{pageSizeMenuMemoized}</div>}
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tableKey,
      store.data,
      store.allData.length,
      store.isPending,
      columns,
      paginationConfig,
      localeConfig,
      pageSizeMenuMemoized,
    ]
  );

  return (
    <Flex vertical gap="large">
      <Flex gap="middle" className={widgets?.search && widgets.date ? `${styles.widgetBlock}` : ""}>
        {widgets?.search && (
          <SearchInput
            placeholder={widgets.searchPlaceholder || "Поиск..."}
            store={store}
            className="full-width"
            id={hintsIds?.search}
          />
        )}
        {widgets?.date && <DateSearchInput store={store} className="full-width" />}
      </Flex>

      {additionalElements}
      <Flex vertical gap="large">
        {shouldShowResetButton && (
          <Flex justify={removeButtonAlign}>
            <CustomButton
              size="small"
              onClick={() => handleResetFilters()}
              tooltip="Сбросить все примененные фильтры"
              type="default"
            >
              Сбросить все фильтры
            </CustomButton>
          </Flex>
        )}
        {whiteBox ? <WhiteBoxBlock>{tableContent}</WhiteBoxBlock> : tableContent}
      </Flex>
    </Flex>
  );
});
